<i18n>
{
  "fr": {
    "tool-title": "Information sur le point d'eau",
    "no-data-title": "Aucune donnée",
    "oid": "Identifiant DB",
    "publicId": "ID cantonal",
    "wellDbk": "ID REGIS",
    "name": "Nom",
    "available-parameters": "Paramètres mesurés",
    "wellLevel": "Niveau piézométrique",
    "springLevel": "Niveau",
    "temp": "Température",
    "ec": "Conductivité électrique",
    "remote-state": "Télétransmission",
    "remote-ok": "A jour",
    "remote-warn": "Plus de données depuis plus de 1 jours",
    "remote-err": "Plus de données depuis plus de 10 jours",
    "no-remote": "pas de télétransmission",
    "remote-nodata": "Aucune donnée reçue",
    "station-title": "Station: ",
    "show-detail": "Voir plus de détail",
    "hide-detail": "Voir moins de détail",
    "sourceNm": "Source",
    "type": "Type",
    "numberOfPiezometers": "Nb. piézomètres",
    "xCrd": "X",
    "yCrd": "Y",
    "communeCd": "Commune",
    "location": "Site",
    "owner": "Propriétaire",
    "remark": "Remarque",
    "close": "Fermer",
    "show-photo-library": "Voir la galerie d'image",
    "geocadast": "ID Geocadast",
    "manage-obs-point": "Gérer le point d'observation"
  },
  "de": {

  }
}
</i18n>

<template>
  <div id="obsPoint-info" class="tool" v-if="show">
    <div class="header">
      <div :class="['header-content', { 'with-image': defaultImageUrl !== null }]">
        <h3>{{ $t('station-title') }} {{ featureName }}</h3>
        <div class="action">
          <md-button :class="['md-icon-button', 'md-raised', 'md-dense']" @click="showImageDialog = true"
            :disabled="images.length === 0">
            <md-icon>photo_library</md-icon>
            <md-tooltip>{{ $t('show-photo-library') }}</md-tooltip>
          </md-button>
          <md-button :class="['md-icon-button', 'md-raised', 'md-dense', { 'md-primary': showDetail }]"
            @click="toggleDetail">
            <md-icon>read_more</md-icon>
            <md-tooltip>{{ $t(showDetail ? 'hide-detail' : 'show-detail') }}</md-tooltip>
          </md-button>
        </div>
      </div>
    </div>


    <div class="main-container">
      <md-empty-state v-if="nodata" class="empty-data" md-rounded md-icon="graphic_eq" :md-label="$t('no-data-title')"
        :md-size="170">
      </md-empty-state>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('wellDbk') }}</h4>
        <span class="info-value">{{ wellDescription.wellDbk }}</span>
      </div>

      <div class="info" v-if="!loading">
        <h4>{{ $t('publicId') }}</h4>
        <span class="info-value">{{ currentObject.publicId }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail && wellDescription.geocadastId !== null">
        <h4>{{ $t('geocadast') }}</h4>
        <span class="info-value"><a :href="wellDescription.geocadastUrl" target="_blank">{{ wellDescription.geocadastId
            }}</a></span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('sourceNm') }}</h4>
        <span class="info-value">{{ wellDescription.sourceNm }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('type') }}</h4>
        <span class="info-value">{{ wellDescription.type }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('numberOfPiezometers') }}</h4>
        <span class="info-value">{{ wellDescription.numberOfPiezometers }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('xCrd') }}</h4>
        <span class="info-value">{{ wellDescription.xCrd | formatCoordinate }}</span>
      </div>

      <div class="info y-crd" v-if="!loading && showDetail">
        <h4>{{ $t('yCrd') }}</h4>
        <span class="info-value">{{ wellDescription.yCrd | formatCoordinate }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('communeCd') }}</h4>
        <span class="info-value">{{ wellDescription.communeCd }}</span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('location') }}</h4>
        <span class="info-value">{{ wellDescription.location }}</span>
      </div>

      <div class="info owner" v-if="!loading && showDetail">
        <h4>{{ $t('owner') }}</h4>
        <span class="info-value">
          {{ wellDescription.ownerNm }}<br />
          <a :href="wellDescription.ownerUrl" target="_blank">{{ wellDescription.ownerUrl }}</a>
        </span>
      </div>

      <div class="info" v-if="!loading && showDetail">
        <h4>{{ $t('remark') }}</h4>
        <span class="info-value">{{ wellDescription.remark | formatNullValue }}</span>
      </div>

      <div class="info available-parameters" v-if="!loading">
        <h4>{{ $t('available-parameters') }}</h4>
        <div class="icon-parameter">
          <div class="level parameter" v-if="hasWellLevel || hasSpringLevel"><md-tooltip>{{ currentObject?.type === 'W'
    ? $t('wellLevel') : $t('springLevel') }}</md-tooltip></div>
          <div class="temp parameter" v-if="hasTemp"><md-tooltip>{{ $t('temp') }}</md-tooltip></div>
          <div class="ec parameter" v-if="hasEc"><md-tooltip>{{ $t('ec') }}</md-tooltip></div>
        </div>
      </div>

      <div class="info remote-state" v-if="!loading">
        <h4>{{ $t('remote-state') }}</h4>
        <md-icon class="no-remote state" v-if="!hasRemote">sensors_off<md-tooltip>{{ $t('no-remote')
            }}</md-tooltip></md-icon>
        <md-icon class="remote-ok state" v-if="hasRemote && workingState === 'ok'">sensors<md-tooltip>{{
    $t('remote-ok') }}</md-tooltip></md-icon>
        <md-icon class="remote-warn state" v-if="hasRemote && workingState === 'warn'">sensors<md-tooltip>{{
    $t('remote-warn') }}</md-tooltip></md-icon>
        <md-icon class="remote-err state" v-if="hasRemote && workingState === 'err'">sensors<md-tooltip>{{
    $t('remote-err') }}</md-tooltip></md-icon>
        <md-icon class="remote-nodata state" v-if="hasRemote && workingState === 'nodata'">sensors<md-tooltip>{{
    $t('remote-nodata') }}</md-tooltip></md-icon>
      </div>

      <div :class="['loading', { 'hide': !loading }]">
        <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5"
          md-mode="indeterminate"></md-progress-spinner>
      </div>

      <md-button class="manage-obs-point md-raised" v-if="isOperator" @click="gotoObsPointAdmin">{{
    $t('manage-obs-point') }}</md-button>
    </div>

    <md-dialog :md-active.sync="showImageDialog" class="image-gallery">
      <md-dialog-title>{{ featureName }}</md-dialog-title>

      <img :src="defaultImageUrl" />

      <md-dialog-actions>
        <md-button class="md-primary" @click="showImageDialog = false">{{ $t('close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>

  </div>
</template>

<script>
import _ from 'lodash'

import AccessToken from '@/utils/accessToken'
import ObservationPointProxy from '@/proxies/observationPoint'
import FileProxy from '@/proxies/file'
import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'
import ToolStoreMixin from '@/mixins/store/tool'

import DefaultObsPointHover from '@/assets/icons/hover-icons/default-obs-point-hover.svg'

import { Style, Icon } from 'ol/style'

export default {
  name: 'ObsPointDescription',
  mixins: [
    MapStoreMixin,
    LayerStoreMixin,
    ToolStoreMixin
  ],
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  data () {
    return  {
      loading: true,
      showDetail: false,
      feature: {},
      regisIds: {},
      wellDescription: {},
      images: [],
      showImageDialog: false,
      hoverFeatureStyle: new Style({
        image: new Icon({
          opacity: 1,
          src: DefaultObsPointHover,
          scale: 0.35
        })
      })
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  beforeDestroy() {
    this.removeTool()
    this.clearHoverFeature()
  },
  watch: {
    currentGeom () {
      this.updateTool()
    },
    selectedLayers () {
      this.updateTool()
    }
  },
  computed: {
    nodata () {
      return !this.loading && _.isEmpty(this.currentObject)
    },
    show () {
      return this.currentGeom !== null && this.selectedLayers.length > 0
    },
    featureName () {
      if (_.isEmpty(this.feature)) return ''
      return this.feature.properties.name
    },
    hasWellLevel () {
      return this.datasources.filter((ds) => { return ds.parameterType.code === 'HEAD_BSL' || ds.parameterType.code === 'HEAD_SFL' }).length > 0
    },
    hasSpringLevel () {
      return this.datasources.filter((ds) => { return ds.parameterType.code === 'WATER_LEVEL' }).length > 0
    },
    hasTemp () {
      return this.datasources.filter((ds) => { return ds.parameterType.code === 'TEMP' }).length > 0
    },
    hasEc () {
      return this.datasources.filter((ds) => { return ds.parameterType.code === 'EC' }).length > 0
    },
    hasRemote () {
      return this.datasources.filter((ds) => ds.datasourceType.code === 'remote').length > 0 && this.currentObject.remoteEnabled
    },
    datasources () {
      if (_.isEmpty(this.currentObject)) return []
      return this.currentObject.datasources
    },
    workingState () {
      let datasources = this.datasources.filter((ds) => _.includes(this.params.parameterTypeCodes, ds.parameterType.code))
      let datasourcesNodata = this.hasRemote ? datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType.code === 'remote') : this.datasources.filter((ds) => ds.workingState === 'nodata' && ds.datasourceType === 'validated')
      let datasourcesWarn = datasources.filter((ds) => ds.workingState === 'warn')
      let datasourcesErr = datasources.filter((ds) => ds.workingState === 'err')

      if (datasourcesNodata.length > 0) return 'nodata'
      if (datasourcesErr.length > 0) return 'err'
      if (datasourcesWarn.length > 0) return 'warn'
      return 'ok'
    },
    defaultImageUrl () {
      if (this.images.length === 0) return null
      return FileProxy.getFileUrl(this.images[0].fileId)
    },
    isOperator () {
      return AccessToken.get().hasRight('strates_operator')
    }
  },
  filters: {
    formatNullValue (value) {
      if (value === null) return '-'
      return value
    },
    formatDepth (value) {
      if (value === null || value === '999.99') return '-'
      return `${value.toFixed(2)} m`
    },
    formatCoordinate (value) {
      if (value === undefined) return ''

      return new Intl.NumberFormat('fr-CH', {
        style: 'decimal'
      }).format(value.toFixed(0)).replaceAll(' ', '\'')
    }
  },
  methods: {
    updateTool () {
      if (this.selectedLayers.length > 0 && this.currentGeom !== null && this.currentObject !== null) {
        this.load()
      } else {
        this.removeTool()
      }
    },
    removeTool () {
      if (this.hoverLayer === null) return

      this.mapRef.removeLayer(this.hoverLayer)
      this.hoverLayer = null
    },
    load () {
      this.showDetail = this.params.showDetail
      this.loading = true
      this.setCurrentObject({})
      this.compute().finally(() => {
        this.loading = false
      })
    },
    async compute () {
      if (this.selectedLayers.length === 0) return

      // SD On ne traite que le premier layer sélectionné
      let layer = _.head(this.selectedLayers)

      let features = await layer.getFeaturesFromGeom(this.mapRef, this.currentGeom)
      if (features.length === 0) return

      // SD On ne traite que la première feature retournée
      this.feature = _.head(features)
      let observationPoint = await ObservationPointProxy.get(this.feature.properties.oid)

      this.setCurrentObject(observationPoint)
      this.setHoverGeoJSON(this.feature)
      this.setHoverStyle(this.hoverFeatureStyle)

      let toFetch = [
        ObservationPointProxy.getRegisIds(this.currentObject.id),
        ObservationPointProxy.getImages(this.currentObject.id)
      ]

      return Promise.allSettled(toFetch).then(async response => {
        //getRegisIds
        if (response[0].status === 'fulfilled') this.regisIds = response[0].value
        else this.regisIds = ''

        //getImages
        if (response[1].status === 'fulfilled') this.images = response[1].value
        else this.images = []

        if (this.showDetail) await this.loadDetail()
      })
    },
    toggleDetail () {
      this.showDetail = !this.showDetail
      if (this.showDetail) {
        this.loading = true
        this.loadDetail().finally(() => {
          this.loading = false
        })
      }
    },
    async loadDetail () {
      this.wellDescription = await ObservationPointProxy.getWellDesc(this.regisIds.wellDbk)
    },
    gotoObsPointAdmin () {
      let url = this.$router.resolve({ name: 'admin-observation-point-data', params: { id: this.currentObject.id } })
      window.open(url.href, '_blank')
    }
  }
}
</script>

<style lang="scss">
.image-gallery {
  .md-dialog-title {
    padding: 12px 12px 0 12px;
  }

  img {
    height: 68vh !important;
    padding: 0 10px 0 10px;
  }
}
</style>

<style lang="scss" scoped>
#obsPoint-info {
  width: 100%;

  .header {
    display: flex;
    flex-direction: column;

    .image-cover {
      margin-top: -10px;
      margin-left: -10px;
      margin-right: -10px;
      margin-bottom: 10px;
      height: 125px;
      overflow: hidden;
      cursor: pointer;

      img {
        display: block;
        object-position: bottom center;
        object-fit: none;
      }
    }

    .header-content {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .close-button {
    display: block;
    margin-left: auto;
    margin-right: 15px;
    margin-bottom: -30px;
    margin-top: 5px;
  }

  .main-container {
    overflow-y: auto;
    overflow-x: hidden;
    background: none;

    padding-left: 23px;
    padding-right: 23px;

    @media (max-height: 500px) {
      max-height: 100vh;
    }
  }

  .layer-name {
    font-size: 14px;
  }

  .container {
    margin-left: -10px;
    margin-right: -10px;
    background-color: white;

    h2 {
      margin-top: 5px;
    }
  }

  .plotly {
    width: 100%;
    height: 250px;
  }

  .empty-data {
    margin-top: 15px;
    margin-bottom: 15px;

    .md-icon {
      width: 80px;
      min-width: 80px;
      font-size: 80px !important;
    }

    .md-empty-state-label {
      font-size: 18px;
      line-height: 18px;
    }

    &.md-empty-state-leave-active {
      display: none !important;
    }
  }

  .loading.hide {
    display: none;
  }

  h3 {
    max-width: 17vw;
  }

  h4 {
    margin: 0px;
    margin-bottom: 5px;
    font-size: 13px;
  }

  .info {
    display: flex;
    align-items: center;
    margin-top: 7px;

    h4 {
      min-width: 140px;
    }

    .info-value {
      margin-left: 5px;
    }

    &.y-crd {
      margin-top: -7px;
    }

    &.owner {
      align-items: flex-start;
    }
  }

  .available-parameters {
    .icon-parameter {
      display: flex;
    }

    .parameter {
      width: 24px;
      height: 24px;
      margin-left: 4px;
      margin-right: 4px;
      border-radius: 12px;

      &.level {
        background: #0066ff;
      }

      &.temp {
        background: #a72d1a;
      }

      &.ec {
        background: #274a13;
      }
    }
  }

  .remote-state {
    .state {
      margin-left: 4px;
      margin-right: 68px;
    }

    .no-remote {
      color: #dcdbe2;
      padding-left: 15px;
    }

    .remote-ok {
      background: #00b050;
      color: white;
    }

    .remote-warn {
      background: #f5c208;
      color: white;
    }

    .remote-err {
      background: #f3210c;
      color: white;
    }

    .remote-nodata {
      background: #a3a3a3;
      color: white;
    }
  }
}

.manage-obs-point {
  margin-left: auto;
  margin-right: auto;
  display: block;
  margin-top: 15px;
}
</style>
